import { useEffect, useState } from 'react';

export function FlowConnections() {
  const [paths, setPaths] = useState<string[]>([]);
  const gradientId = 'flow-conn-grad';
  const [gradientEnd, setGradientEnd] = useState({ x2: 1, y2: 1 });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const build = () => {
      const svg = document.getElementById('flow-svg');
      if (!svg) return;
      const svgRect = svg.getBoundingClientRect();

      const getRect = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return null;
        const r = el.getBoundingClientRect();

        return {
          top: r.top - svgRect.top,
          left: r.left - svgRect.left,
          right: r.right - svgRect.left,
          bottom: r.bottom - svgRect.top,
          width: r.width,
          height: r.height,
          centerX: r.left - svgRect.left + r.width / 2,
          centerY: r.top - svgRect.top + r.height / 2,
        };
      };

      const getChildrenRects = (parentId: string, selector?: string) => {
        const parent = document.getElementById(parentId);
        if (!parent) return [];
        const nodes = selector ? Array.from(parent.querySelectorAll(selector)) : Array.from(parent.children);
        const rects = nodes.map((el: Element) => {
          const r = el.getBoundingClientRect();
          return {
            top: r.top - svgRect.top,
            left: r.left - svgRect.left,
            right: r.right - svgRect.left,
            bottom: r.bottom - svgRect.top,
            width: r.width,
            height: r.height,
            centerX: r.left - svgRect.left + r.width / 2,
            centerY: r.top - svgRect.top + r.height / 2,
            node: el,
          };
        });
        return rects;
      };

      // Main rects
      const f1 = getRect('flow-1');
      const where = getRect('flow-1-where');
      const f2 = getRect('flow-2');
      const f3 = getRect('flow-3');
      const f5 = getRect('flow-5');
      const f6 = getRect('flow-6');
      const f7 = getRect('flow-7');
      // Item rects
      const f3Items = getChildrenRects('flow-3');
      const f4Items = getChildrenRects('flow-4');
      const f8Items = getChildrenRects('flow-8');

      const svgW = svgRect.width;
      const farRight = Math.max(svgW - 20, 0);
      const farLeft = 20;

      const maxX = Math.max(
        0,
        farRight,
        f1?.right ?? 0,
        f2?.right ?? 0,
        f3?.right ?? 0,
        f5?.right ?? 0,
        f6?.right ?? 0,
        f7?.right ?? 0,
        ...f3Items.map(it => it.right),
        ...f4Items.map(it => it.right),
        ...f8Items.map(it => it.right),
      );
      const maxY = Math.max(
        0,
        f1?.bottom ?? 0,
        f2?.bottom ?? 0,
        f3?.bottom ?? 0,
        f5?.bottom ?? 0,
        f6?.bottom ?? 0,
        f7?.bottom ?? 0,
        ...f3Items.map(it => it.bottom),
        ...f4Items.map(it => it.bottom),
        ...f8Items.map(it => it.bottom),
      );

      setGradientEnd(prev => {
        const nextX = maxX || 1;
        const nextY = maxY || 1;
        if (Math.abs(prev.x2 - nextX) < 1 && Math.abs(prev.y2 - nextY) < 1) return prev;
        return { x2: nextX, y2: nextY };
      });

      const newPaths: string[] = [];
      const cornerRadius = 18;

      type Pt = { x: number; y: number };
      const fmt = (n: number) => Number(n.toFixed(3));

      const manhattanPath = (pts: Pt[], radius: number) => {
        if (pts.length < 2) return '';
        const safeRadius = Math.max(0, radius);

        const straight = (a: Pt, b: Pt) =>
          a.x === b.x || a.y === b.y ? `L ${fmt(b.x)} ${fmt(b.y)}` : `L ${fmt(b.x)} ${fmt(b.y)}`;

        if (pts.length === 2) {
          const a = pts[0];
          const b = pts[1];
          return `M ${fmt(a.x)} ${fmt(a.y)} ${straight(a, b)}`.trim();
        }

        let d = `M ${fmt(pts[0].x)} ${fmt(pts[0].y)}`;
        let last: Pt = { ...pts[0] };

        for (let i = 1; i < pts.length - 1; i += 1) {
          const prev = pts[i - 1];
          const curr = pts[i];
          const next = pts[i + 1];

          const incomingAxis = prev.x !== curr.x ? 'x' : 'y';
          const outgoingAxis = curr.x !== next.x ? 'x' : 'y';

          if (incomingAxis === outgoingAxis) {
            if (curr.x !== last.x || curr.y !== last.y) {
              d += ` ${straight(last, curr)}`;
              last = { ...curr };
            }
            continue;
          }

          const dirIn = incomingAxis === 'x' ? Math.sign(curr.x - prev.x) : Math.sign(curr.y - prev.y);
          const dirOut = outgoingAxis === 'x' ? Math.sign(next.x - curr.x) : Math.sign(next.y - curr.y);

          const distIn = incomingAxis === 'x' ? Math.abs(curr.x - prev.x) : Math.abs(curr.y - prev.y);
          const distOut = outgoingAxis === 'x' ? Math.abs(next.x - curr.x) : Math.abs(next.y - curr.y);

          const off = Math.min(safeRadius, distIn / 2, distOut / 2);

          if (off <= 0.01) {
            if (curr.x !== last.x || curr.y !== last.y) {
              d += ` ${straight(last, curr)}`;
            }
            last = { ...curr };
            continue;
          }

          const before: Pt =
            incomingAxis === 'x'
              ? { x: curr.x - dirIn * off, y: curr.y }
              : { x: curr.x, y: curr.y - dirIn * off };

          const after: Pt =
            outgoingAxis === 'x'
              ? { x: curr.x + dirOut * off, y: curr.y }
              : { x: curr.x, y: curr.y + dirOut * off };

          if (before.x !== last.x || before.y !== last.y) {
            d += ` ${straight(last, before)}`;
          }

          d += ` Q ${fmt(curr.x)} ${fmt(curr.y)} ${fmt(after.x)} ${fmt(after.y)}`;
          last = after;
        }

        const end = pts[pts.length - 1];
        if (end.x !== last.x || end.y !== last.y) {
          d += ` ${straight(last, end)}`;
        }

        return d.trim();
      };

      // 1. flow-1 → flow-2
      if (f2 && (where || f1)) {
        const anchor = where ?? f1!;
        const xStart = anchor.centerX;
        const y1 = anchor.bottom;
        const y2 = f2.centerY;
        const x2 = f2.left;
        newPaths.push(
          manhattanPath(
            [
              { x: xStart, y: y1 },
              { x: xStart, y: y2 },
              { x: x2, y: y2 },
            ],
            cornerRadius,
          ),
        );
      }

      // 2. flow-2 → flow-3
      if (f2 && f3) {
        const x1 = f2.right;
        const y1 = f2.centerY;

        const xSnake = Math.min(f2.right + 240, farRight);
        const targetIdx = f3Items.length >= 3 ? 2 : Math.max(0, f3Items.length - 1);
        const target = f3Items[targetIdx];
        const x2 = target?.right ?? 500;
        const y2 = target?.centerY ?? f3.centerY;
        newPaths.push(
          manhattanPath(
            [
              { x: x1, y: y1 },
              { x: xSnake, y: y1 },
              { x: xSnake, y: y2 },
              { x: x2, y: y2 },
            ],
            cornerRadius,
          ),
        );
      }

      // 2.5. flow-3
      if (f3Items.length >= 3) {
        const y = f3Items[1].centerY;
        newPaths.push(`M ${f3Items[0].right} ${y} L ${f3Items[1].left} ${y}`);
        newPaths.push(`M ${f3Items[1].right} ${y} L ${f3Items[2].left} ${y}`);
      }

      // 3. flow-3
      if (f3Items.length === 3 && f4Items.length === 4) {
        const mid = f3Items[1];
        f4Items.forEach((box, idx) => {
          const yCorner = idx === 0 || idx === 3 ? mid.bottom + 50 : mid.bottom + 50 + 20;
          newPaths.push(
            manhattanPath(
              [
                { x: mid.centerX, y: mid.bottom },
                { x: mid.centerX, y: yCorner },
                { x: box.centerX, y: yCorner },
                { x: box.centerX, y: box.top },
              ],
              cornerRadius,
            ),
          );
        });
      }

      // 4. flow-3 → flow-5
      if (f3 && f5 && f3Items.length >= 1) {
        const x1 = f3Items[0].left;
        const y1 = f3Items.length >= 3 ? (f3Items[0].centerY + f3Items[2].centerY) / 2 : f3Items[0].centerY;
        const y2 = f5.centerY;
        const endX = f5.left;
        const spineX = Math.max(x1 - 240, farLeft);
        const pts: Pt[] =
          Math.abs(spineX - endX) < 0.5
            ? [
                { x: x1, y: y1 },
                { x: spineX, y: y1 },
                { x: spineX, y: y2 },
              ]
            : [
                { x: x1, y: y1 },
                { x: spineX, y: y1 },
                { x: spineX, y: y2 },
                { x: endX, y: y2 },
              ];
        newPaths.push(manhattanPath(pts, cornerRadius));
      }

      // 5. flow-5 → flow-6
      if (f5 && f6) {
        const xOut = Math.min(f5.right + 160, farRight);
        const midY = (f5.bottom + f6.top) / 2;
        newPaths.push(
          manhattanPath(
            [
              { x: f5.right, y: f5.centerY },
              { x: xOut, y: f5.centerY },
              { x: xOut, y: midY },
              { x: f6.centerX, y: (f5.bottom + f6.top) / 2 },
              { x: f6.centerX, y: f6.top },
            ],
            cornerRadius,
          ),
        );
      }

      // 6. flow-6 → flow-7
      if (f6 && f7) {
        newPaths.push(
          manhattanPath(
            [
              { x: f6.centerX, y: f6.bottom },
              { x: f6.centerX, y: f7.top },
              { x: f7.centerX, y: f7.top },
            ],
            cornerRadius,
          ),
        );
      }

      // 7. flow-7 → each flow-8 item
      if (f7 && f8Items.length === 3) {
        f8Items.forEach(box => {
          const yCorner = box.top - 66;
          newPaths.push(
            manhattanPath(
              [
                { x: f7.centerX, y: f7.bottom },
                { x: f7.centerX, y: yCorner },
                { x: box.centerX, y: yCorner },
                { x: box.centerX, y: box.top },
              ],
              cornerRadius,
            ),
          );
        });
      }

      setPaths(newPaths);
    };

    const scheduleBuild = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(build);
      });
    };

    scheduleBuild();
    window.addEventListener('resize', scheduleBuild);

    return () => window.removeEventListener('resize', scheduleBuild);
  }, []);

  useEffect(() => {
    if (hasAnimated) return;
    if (paths.length === 0) return;

    const svg = document.getElementById('flow-svg');
    if (!svg) return;

    const pathEls = Array.from(svg.querySelectorAll('path')) as SVGPathElement[];
    if (pathEls.length !== paths.length) return;

    const flowRevealByPathIndex: Record<number, string> = {
      0: 'flow-2',
      1: 'flow-3',
      4: 'flow-4',
      5: 'flow-4',
      6: 'flow-4',
      7: 'flow-4',
      8: 'flow-5',
      9: 'flow-6',
      10: 'flow-7',
      11: 'flow-8',
    };

    const sectionIds = ['flow-2', 'flow-3', 'flow-4', 'flow-5', 'flow-6', 'flow-7', 'flow-8'];
    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(10px)';
      el.style.transition = 'opacity 600ms ease, transform 600ms ease';
    });

    const speedPxPerMs = 0.9;
    const gapMs = 80;
    let t = 0;

    const reveal = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    };

    pathEls.forEach((pathEl, i) => {
      const len = pathEl.getTotalLength();
      const duration = Math.min(2000, Math.max(450, len / speedPxPerMs));

      pathEl.style.strokeDasharray = `${len}`;
      pathEl.style.strokeDashoffset = `${len}`;
      pathEl.style.opacity = '1';

      pathEl.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], {
        duration,
        delay: t,
        easing: 'ease-in-out',
        fill: 'forwards',
      });

      const revealId = flowRevealByPathIndex[i];
      if (revealId) window.setTimeout(() => reveal(revealId), t + duration);

      t += duration + gapMs;
    });

    setHasAnimated(true);
  }, [paths, hasAnimated]);

  return (
    <svg
      id="flow-svg"
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2={gradientEnd.x2}
          y2={gradientEnd.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="var(--pink-color)" />
          <stop offset="100%" stopColor="var(--blue-color)" />
        </linearGradient>
      </defs>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke={`url(#${gradientId})`}
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
